import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useAuth } from '../../hooks';
import {
  isOrgScopedPathname,
  resolveOrgSwitchTargetPath,
} from './orgRouteUtils';

export type UseRouteOrgSyncedOptions = {
  routeOrg: string | undefined;
};

export type UseRouteOrgSyncedResult = {
  ready: boolean;
  isLoading: boolean;
  isUnknownRouteOrg: boolean;
};

export const useRouteOrgSynced = ({
  routeOrg,
}: UseRouteOrgSyncedOptions): UseRouteOrgSyncedResult => {
  const history = useHistory();
  const location = useLocation();
  const {
    orgs,
    organization_id: selectedOrg,
    setSelectedOrg,
    isLoading,
  } = useAuth();

  const isKnownOrg = Boolean(
    routeOrg && !isLoading && orgs.some(o => o.id === routeOrg)
  );
  const isUnknownRouteOrg = Boolean(
    routeOrg && !isLoading && !orgs.some(o => o.id === routeOrg)
  );
  const orgMismatch = isKnownOrg && routeOrg !== selectedOrg;

  useEffect(() => {
    if (orgMismatch && routeOrg) {
      setSelectedOrg(routeOrg);
    }
  }, [orgMismatch, routeOrg, setSelectedOrg]);

  useEffect(() => {
    if (!isUnknownRouteOrg || !routeOrg) return;

    const fallbackOrg = selectedOrg ?? orgs[0]?.id;
    if (!fallbackOrg || !isOrgScopedPathname(location.pathname)) return;

    const target = resolveOrgSwitchTargetPath(location.pathname, fallbackOrg);

    if (target) {
      history.replace(target);
    }
  }, [
    isUnknownRouteOrg,
    routeOrg,
    selectedOrg,
    orgs,
    location.pathname,
    history,
  ]);

  const ready = isKnownOrg && !orgMismatch;

  return { ready, isLoading, isUnknownRouteOrg };
};
